import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import styled from 'styled-components';
import colors from '../theme/colors';

const SliderWrap = styled.div`
  position: relative;
  margin: 2.5rem 0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(30, 30, 30, 0.12);
  background: ${colors.mainWhite};
`;

const Track = styled.div<{ $current: number }>`
  display: flex;
  align-items: flex-start;
  transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transform: translateX(${props => -props.$current * 100}%);
`;

const Viewport = styled.div<{ $height?: number }>`
  position: relative;
  line-height: 0;
  font-size: 0;
  background: ${colors.mainWhite};
  overflow: hidden;
  ${props =>
    props.$height != null && props.$height > 0
      ? `height: ${props.$height}px;`
      : ''}
`;

const Slide = styled.div`
  flex: 0 0 100%;
  width: 100%;
  min-width: 0;
  position: relative;

  img {
    width: 100%;
    height: auto;
    max-height: 420px;
    object-fit: cover;
    object-position: center center;
    display: block;
    vertical-align: top;
  }
`;

const SliderCaption = styled.figcaption`
  margin: 0;
  padding: 0.4rem 0.75rem 0.35rem;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: ${colors.textGray};
  font-style: italic;
  text-align: center;
  background: ${colors.goldGrey};
`;

const NavButton = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${props => (props.$side === 'left' ? 'left: 0.75rem' : 'right: 0.75rem')};
  transform: translateY(-50%);
  z-index: 2;
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  color: ${colors.mainBlack};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${colors.mainWhite};
    color: ${colors.mainOrange};
  }

  &:focus-visible {
    outline: 2px solid ${colors.mainOrange};
    outline-offset: 2px;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const Dots = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  z-index: 2;
`;

const Dot = styled.button<{ $active?: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  padding: 0;
  cursor: pointer;
  background: ${props => (props.$active ? colors.mainOrange : 'rgba(255, 255, 255, 0.7)')};
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: ${props => (props.$active ? colors.mainOrangeHover : 'rgba(255, 255, 255, 0.95)')};
  }

  &:focus-visible {
    outline: 2px solid ${colors.mainWhite};
    outline-offset: 2px;
  }
`;

export interface SliderImageItem {
  url: string;
  alt?: string;
  caption?: string;
}

interface SliderProps {
  images: SliderImageItem[];
  autoplayIntervalMs?: number;
  showArrows?: boolean;
  showDots?: boolean;
}

const Slider: React.FC<SliderProps> = ({
  images,
  autoplayIntervalMs = 5000,
  showArrows = true,
  showDots = true,
}) => {
  const [current, setCurrent] = useState(0);
  const [viewportHeight, setViewportHeight] = useState<number | undefined>(undefined);
  const viewportRef = useRef<HTMLDivElement>(null);
  const total = images.length;
  const imageUrlsKey = images.map(i => i.url).join('\0');

  useEffect(() => {
    if (total > 0 && current >= total) setCurrent(0);
  }, [current, total]);

  const goTo = useCallback(
    (index: number) => {
      setCurrent((total + index) % total);
    },
    [total]
  );

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  useEffect(() => {
    if (total <= 1 || !autoplayIntervalMs) return;
    const t = setInterval(next, autoplayIntervalMs);
    return () => clearInterval(t);
  }, [total, autoplayIntervalMs, current, next]);

  useLayoutEffect(() => {
    const root = viewportRef.current;
    if (!root || !total) return;

    const slide = root.querySelector(`[data-slide-index="${current}"]`);
    const img = slide?.querySelector('img') as HTMLImageElement | null;
    if (!img) return;

    const syncHeight = () => {
      const h = Math.round(img.offsetHeight);
      if (h > 0) setViewportHeight(h);
    };

    syncHeight();
    img.addEventListener('load', syncHeight);

    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => syncHeight()) : null;
    ro?.observe(img);

    const onWinResize = () => syncHeight();
    window.addEventListener('resize', onWinResize);

    return () => {
      img.removeEventListener('load', syncHeight);
      ro?.disconnect();
      window.removeEventListener('resize', onWinResize);
    };
  }, [current, total, imageUrlsKey]);

  const currentCaption = images[current]?.caption?.trim();

  if (!images.length) return null;

  const showNav = total > 1 && showArrows;
  const showDotRow = total > 1 && showDots;

  return (
    <SliderWrap>
      <Viewport ref={viewportRef} $height={viewportHeight}>
        <Track $current={current}>
          {images.map((img, i) => (
            <Slide key={`${img.url}-${i}`} data-slide-index={i}>
              <img src={img.url} alt={img.alt || `Слайд ${i + 1}`} />
            </Slide>
          ))}
        </Track>

        {showNav && (
          <>
            <NavButton $side="left" onClick={prev} aria-label="Попередній слайд">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </NavButton>
            <NavButton $side="right" onClick={next} aria-label="Наступний слайд">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </NavButton>
          </>
        )}

        {showDotRow && (
          <Dots>
            {images.map((_, i) => (
              <Dot key={i} $active={i === current} onClick={() => goTo(i)} aria-label={`Слайд ${i + 1}`} />
            ))}
          </Dots>
        )}
      </Viewport>
      {currentCaption ? <SliderCaption>{currentCaption}</SliderCaption> : null}
    </SliderWrap>
  );
};

export default Slider;
